import { useContext } from "react";
import LinkIcon from "@mui/icons-material/Link";
import { Box, Button, MenuItem, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { v4 } from "uuid";

import type { ExtendedPatient } from "@thalamos/aspire-common";

import { Banner, BannerList } from "../../Banner/Banner.js";
import { PopupDialog } from "../../PopupDialog/PopupDialog.js";
import { PopupDialogTitle } from "../../PopupDialog/PopupDialogTitle.js";
import { api } from "~/api.js";
import { LoggedInUserContext } from "~/Contexts.js";
import type { DialogProps } from "~/hooks/dialogs/index.js";
import { routeFns } from "~/routePaths.js";

export const CreateRioLinkButton = ({
  patient,
  displayMode,
}: {
  patient: Pick<ExtendedPatient, "nhsNumber">;
  displayMode: "button" | "menu-item";
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const nhsNumber = patient.nhsNumber!;
  const userContext = useContext(LoggedInUserContext);
  const enabledRio =
    userContext?.user?.sessionOrganisationConfiguration?.rioInstancesEnabled[0];

  // Whether to offer the link at all is the caller's decision, so the button
  // renders regardless and only the click needs an instance to point at.
  const createLink = () => {
    if (enabledRio === undefined) {
      throw new Error("No Rio instance is enabled for this organisation");
    }

    void (async () => {
      const token = v4();

      await api.rio.clickThrough.createAndClaimToken({
        externalSystemType: "rio",
        externalSystemId: enabledRio.instanceId,
        externalPatientIdType: "nhs.number",
        externalPatientIdValue: nhsNumber,
        token: token,
      });

      navigate(routeFns.patientCreateFromExternalId(token));
    })();
  };

  return displayMode === "menu-item" ? (
    <MenuItem
      sx={{
        justifyContent: "flex-start",
        py: 1.5,
        minWidth: "240px",
        gap: 1,
        textTransform: "none",
      }}
      onClick={createLink}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "1.5rem",
          color: "text.secondary",
        }}
      >
        <LinkIcon sx={{ display: "flex", width: "1.5rem" }} />
      </Box>
      <Typography noWrap>
        {t("components.externalPatientLink.createLinkButton.linkWithRio")}
      </Typography>
    </MenuItem>
  ) : (
    <Button
      variant="outlined"
      startIcon={<LinkIcon />}
      sx={{ minWidth: "max-content" }}
      onClick={createLink}
    >
      {t("components.externalPatientLink.createLinkButton.linkWithRio")}
    </Button>
  );
};

export const LinkToPdsButton = ({
  patient,
  displayMode,
}: {
  patient: Pick<ExtendedPatient, "id">;
  displayMode: "button" | "menu-item";
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return displayMode === "menu-item" ? (
    <MenuItem
      sx={{
        justifyContent: "flex-start",
        py: 1.5,
        minWidth: "240px",
        gap: 1,
        textTransform: "none",
      }}
      onClick={async () => {
        navigate(routeFns.patientLinkToPds(patient.id));
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "1.5rem",
          color: "text.secondary",
        }}
      >
        <LinkIcon sx={{ display: "flex", width: "1.5rem" }} />
      </Box>
      <Typography noWrap>
        {t("components.externalPatientLink.createLinkButton.linkWithPds")}
      </Typography>
    </MenuItem>
  ) : (
    <Button
      variant="outlined"
      startIcon={<LinkIcon />}
      sx={{ minWidth: "max-content" }}
      onClick={async () => {
        navigate(routeFns.patientLinkToPds(patient.id));
      }}
    >
      {t("components.externalPatientLink.createLinkButton.linkWithPds")}
    </Button>
  );
};
export function CreateLinkDialog({ open, onClose }: DialogProps) {
  const { t } = useTranslation();

  return (
    <PopupDialog open={open} onClose={() => onClose()}>
      <PopupDialogTitle
        titleText={t(
          "components.externalPatientLink.createLinkButton.dialog.title",
        )}
        closeDialog={onClose}
      />

      <Banner
        bannerType={BannerList.WARNING}
        body={t(
          "components.externalPatientLink.createLinkButton.dialog.body",
        ).split("\n")}
      />

      <Stack
        sx={{
          display: "flex",
          marginTop: "2rem",
          justifyContent: "centre",
          alignItems: "center",
        }}
      >
        <Button variant="contained" onClick={() => onClose()}>
          Close
        </Button>
      </Stack>
    </PopupDialog>
  );
}
